import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { doc, getDoc, setDoc, collection, getDocs, query, where, orderBy, limit, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface PlatformLineConfig {
  liffId: string;
  channelId: string;
  channelSecret: string;
  channelAccessToken: string;
  enabled: boolean;
  friendAddUrl: string;
}

interface NotificationHistory {
  id: string;
  type: string;
  title: string;
  message: string;
  sentCount: number;
  successCount: number;
  failureCount: number;
  status: string;
  sentAt?: Date;
  createdAt: Date;
}

const PlatformLineSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 設定
  const [config, setConfig] = useState<PlatformLineConfig>({
    liffId: '',
    channelId: '',
    channelSecret: '',
    channelAccessToken: '',
    enabled: false,
    friendAddUrl: '',
  });

  // 通知フォーム
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'new_shop' | 'campaign' | 'maintenance' | 'custom'>('custom');

  // 統計
  const [connectedUserCount, setConnectedUserCount] = useState(0);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistory[]>([]);

  // 確認ダイアログ
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchNotificationHistory();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'platformSettings', 'main'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.platformLine) {
          setConfig({
            liffId: data.platformLine.liffId || '',
            channelId: data.platformLine.channelId || '',
            channelSecret: data.platformLine.channelSecret || '',
            channelAccessToken: data.platformLine.channelAccessToken || '',
            enabled: data.platformLine.enabled || false,
            friendAddUrl: data.platformLine.friendAddUrl || '',
          });
        }
      }
    } catch (err) {
      console.error('設定取得エラー:', err);
      setError('設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // プラットフォームLINE連携ユーザー数を取得
      const usersQuery = query(
        collection(db, 'users'),
        where('platformLineId', '!=', null)
      );
      const usersSnapshot = await getDocs(usersQuery);
      setConnectedUserCount(usersSnapshot.size);
    } catch (err) {
      console.error('統計取得エラー:', err);
    }
  };

  const fetchNotificationHistory = async () => {
    try {
      const historyQuery = query(
        collection(db, 'platformNotifications'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const historySnapshot = await getDocs(historyQuery);
      const history: NotificationHistory[] = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sentAt: doc.data().sentAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
      })) as NotificationHistory[];
      setNotificationHistory(history);
    } catch (err) {
      console.error('通知履歴取得エラー:', err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      await setDoc(doc(db, 'platformSettings', 'main'), {
        platformLine: {
          liffId: config.liffId,
          channelId: config.channelId,
          channelSecret: config.channelSecret,
          channelAccessToken: config.channelAccessToken,
          enabled: config.enabled,
          friendAddUrl: config.friendAddUrl,
        },
        updatedAt: Timestamp.now(),
      }, { merge: true });

      setSuccess('設定を保存しました');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('設定保存エラー:', err);
      setError('設定の保存に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle || !notificationMessage) {
      setError('タイトルとメッセージを入力してください');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setOpenConfirmDialog(false);

      // 通知ログを作成
      const notificationRef = await addDoc(collection(db, 'platformNotifications'), {
        type: notificationType,
        title: notificationTitle,
        message: notificationMessage,
        targetType: 'all',
        sentCount: 0,
        successCount: 0,
        failureCount: 0,
        status: 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Cloud Functionを呼び出して一括送信
      const functions = getFunctions();
      const sendPlatformLineNotification = httpsCallable(functions, 'sendPlatformLineNotification');

      try {
        const result = await sendPlatformLineNotification({
          notificationId: notificationRef.id,
          title: notificationTitle,
          message: notificationMessage,
        });

        setSuccess(`通知を送信しました`);
        setNotificationTitle('');
        setNotificationMessage('');
        fetchNotificationHistory();
      } catch (funcError: any) {
        // Cloud Functionがまだ存在しない場合のフォールバック
        console.log('Cloud Function未実装、ログのみ保存:', funcError);
        setSuccess('通知ログを保存しました（送信機能は後日実装）');
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('通知送信エラー:', err);
      setError('通知の送信に失敗しました: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="完了" color="success" size="small" />;
      case 'sending':
        return <Chip label="送信中" color="warning" size="small" />;
      case 'failed':
        return <Chip label="失敗" color="error" size="small" />;
      default:
        return <Chip label="保留" color="default" size="small" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'new_shop': return '新店舗';
      case 'campaign': return 'キャンペーン';
      case 'maintenance': return 'メンテナンス';
      default: return 'カスタム';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          プラットフォームLINE設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          システム全体のLINE公式アカウントを管理し、ユーザーへ一括通知を送信できます
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 統計カード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{connectedUserCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    LINE連携ユーザー
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{notificationHistory.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    送信済み通知
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: config.enabled ? 'success.light' : 'grey.200' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: config.enabled ? 'success.main' : 'grey.500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  {config.enabled ? '✓' : '×'}
                </Box>
                <Box>
                  <Typography variant="h6">
                    {config.enabled ? '有効' : '無効'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    LINE連携ステータス
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="LINE設定" />
          <Tab label="通知配信" />
          <Tab label="配信履歴" />
        </Tabs>

        {/* LINE設定タブ */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  />
                }
                label="プラットフォームLINEを有効にする"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="LIFF ID"
                value={config.liffId}
                onChange={(e) => setConfig({ ...config, liffId: e.target.value })}
                placeholder="1234567890-xxxxxxxx"
                helperText="LINE Developersで取得したLIFF ID"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="チャンネルID"
                value={config.channelId}
                onChange={(e) => setConfig({ ...config, channelId: e.target.value })}
                placeholder="1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="チャンネルシークレット"
                type="password"
                value={config.channelSecret}
                onChange={(e) => setConfig({ ...config, channelSecret: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="チャンネルアクセストークン"
                type="password"
                value={config.channelAccessToken}
                onChange={(e) => setConfig({ ...config, channelAccessToken: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="友だち追加URL"
                value={config.friendAddUrl}
                onChange={(e) => setConfig({ ...config, friendAddUrl: e.target.value })}
                placeholder="https://line.me/R/ti/p/@xxxxx"
                helperText="LINE公式アカウントの友だち追加URL"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? '保存中...' : '設定を保存'}
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 通知配信タブ */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                LINE連携済みのユーザー {connectedUserCount} 名に通知を送信します
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="通知タイトル"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                placeholder="例: 新店舗オープンのお知らせ"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="通知タイプ"
                value={notificationType}
                onChange={(e) => setNotificationType(e.target.value as any)}
                SelectProps={{ native: true }}
              >
                <option value="new_shop">新店舗オープン</option>
                <option value="campaign">キャンペーン</option>
                <option value="maintenance">メンテナンス</option>
                <option value="custom">カスタム</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="メッセージ本文"
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                placeholder="通知メッセージを入力してください..."
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => setOpenConfirmDialog(true)}
                disabled={!config.enabled || connectedUserCount === 0 || !notificationTitle || !notificationMessage}
              >
                通知を送信
              </Button>
              {!config.enabled && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  LINE設定が無効です
                </Typography>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* 配信履歴タブ */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchNotificationHistory}
            >
              更新
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell>タイプ</TableCell>
                  <TableCell>タイトル</TableCell>
                  <TableCell align="right">送信数</TableCell>
                  <TableCell align="right">成功</TableCell>
                  <TableCell align="right">失敗</TableCell>
                  <TableCell>ステータス</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        配信履歴がありません
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  notificationHistory.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>
                        {notification.createdAt?.toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>{getTypeLabel(notification.type)}</TableCell>
                      <TableCell>{notification.title}</TableCell>
                      <TableCell align="right">{notification.sentCount}</TableCell>
                      <TableCell align="right">{notification.successCount}</TableCell>
                      <TableCell align="right">{notification.failureCount}</TableCell>
                      <TableCell>{getStatusChip(notification.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* 送信確認ダイアログ */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>通知を送信しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            以下の内容でLINE連携済みユーザー {connectedUserCount} 名に通知を送信します。
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {notificationTitle}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {notificationMessage}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendNotification}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {sending ? '送信中...' : '送信する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlatformLineSettings;
